import {ResolvedBookmark, ResolvedNavNode, flattenLinks} from './nav-tree';

/**
 * Menu search: every link in both menus plus the page bookmarks, matched on the label, the
 * groups above it and the server's keywords. Diacritics fold away ("snoball" finds
 * "Snöball"), every word typed has to match somewhere, and the label outranks the rest.
 */

export interface NavSearchEntry {
  key: string;
  label: string;
  /** The groups above it (or "Favorites"), shown under the label and searched too. */
  trail: string[];
  keywords: string[];
  icon: string | null;
  path: string | null;
  queryParams: {[key: string]: string} | null;
  href: string | null;
  external: boolean;
  favorite: boolean;
  node: ResolvedNavNode | null;
  bookmark: ResolvedBookmark | null;
}

export interface NavSearchResult {
  entry: NavSearchEntry;
  score: number;
  /** Where the query matched in the label, for highlighting. */
  ranges: Array<[number, number]>;
}

export interface HighlightSegment {
  text: string;
  match: boolean;
}

/** Lower-cased and stripped of diacritics, with a map from each folded character back to the original. */
export function foldWithMap(value: string): {text: string; map: number[]} {
  let text = '';
  const map: number[] = [];
  Array.from(value || '').forEach((character, index) => {
    const folded = character.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();
    for (let offset = 0; offset < folded.length; offset++) {
      text += folded[offset];
      map.push(index);
    }
  });
  return {text, map};
}

const fold = (value: string) => foldWithMap(value).text;

export function buildSearchIndex(main: ResolvedNavNode[], user: ResolvedNavNode[],
                                 bookmarks: ResolvedBookmark[]): NavSearchEntry[] {
  const favorites = new Set(bookmarks.filter((bookmark) => bookmark.node).map((bookmark) => bookmark.node.id));
  const fromNodes = flattenLinks([...main, ...user]).map((node): NavSearchEntry => ({
    key: `menu:${node.id}`,
    label: node.label,
    trail: node.menu === 'user' ? ['Account', ...node.ancestors] : node.ancestors,
    keywords: node.keywords,
    icon: node.icon,
    path: node.path,
    queryParams: node.queryParams,
    href: node.href,
    external: node.external,
    favorite: favorites.has(node.id),
    node,
    bookmark: null,
  }));
  const pages = bookmarks.filter((bookmark) => !bookmark.node).map((bookmark): NavSearchEntry => ({
    key: bookmark.key,
    label: bookmark.label,
    trail: ['Favorites'],
    keywords: [],
    icon: bookmark.icon,
    path: bookmark.path,
    queryParams: bookmark.queryParams,
    href: null,
    external: false,
    favorite: true,
    node: null,
    bookmark,
  }));
  return [...fromNodes, ...pages];
}

const WORD_START = /[\s\-(/›]/;

/** Points for one typed word against one entry, and where it matched the label; 0 is no match. */
function scoreToken(token: string, entry: NavSearchEntry, label: {text: string; map: number[]}):
  {score: number; range: [number, number] | null} {
  const at = label.text.indexOf(token);
  const range = (start: number): [number, number] =>
    [label.map[start], label.map[start + token.length - 1] + 1];
  if (label.text === token) return {score: 120, range: range(0)};
  if (at === 0) return {score: 100, range: range(0)};
  for (let index = at; index > 0; index = label.text.indexOf(token, index + 1)) {
    if (WORD_START.test(label.text[index - 1])) return {score: 80, range: range(index)};
  }
  if (at > 0) return {score: 60, range: range(at)};
  const keywords = entry.keywords.map(fold);
  if (keywords.some((keyword) => keyword.startsWith(token))) return {score: 50, range: null};
  if (keywords.some((keyword) => keyword.includes(token))) return {score: 40, range: null};
  if (entry.trail.some((part) => fold(part).includes(token))) return {score: 20, range: null};
  // A subsequence ("msp" for Mailer Service Providers), worth less the more it is spread out.
  let position = -1;
  let gaps = 0;
  for (const character of token) {
    const next = label.text.indexOf(character, position + 1);
    if (next < 0) return {score: 0, range: null};
    if (position >= 0) gaps += next - position - 1;
    position = next;
  }
  return {score: 10 + Math.max(0, 10 - gaps), range: null};
}

export function searchMenu(index: NavSearchEntry[], query: string, limit = 20): NavSearchResult[] {
  const tokens = fold(query || '').split(/\s+/).filter(Boolean);
  if (!tokens.length) return [];
  const results: Array<NavSearchResult & {order: number}> = [];
  index.forEach((entry, order) => {
    const label = foldWithMap(entry.label);
    let score = 0;
    const ranges: Array<[number, number]> = [];
    for (const token of tokens) {
      const match = scoreToken(token, entry, label);
      if (!match.score) return;
      score += match.score;
      if (match.range) ranges.push(match.range);
    }
    if (entry.favorite) score += 5;
    results.push({entry, score, ranges, order});
  });
  return results
    .sort((a, b) => (b.score - a.score) || (a.entry.label.length - b.entry.label.length) || (a.order - b.order))
    .slice(0, limit)
    .map(({entry, score, ranges}) => ({entry, score, ranges}));
}

/** The label cut into matched and unmatched runs - rendered as text, never as HTML. */
export function highlightSegments(label: string, ranges: Array<[number, number]>): HighlightSegment[] {
  const sorted = [...(ranges || [])].sort((a, b) => a[0] - b[0]);
  const segments: HighlightSegment[] = [];
  let cursor = 0;
  sorted.forEach(([start, end]) => {
    if (end <= cursor) return;
    const from = Math.max(start, cursor);
    if (from > cursor) segments.push({text: label.slice(cursor, from), match: false});
    segments.push({text: label.slice(from, end), match: true});
    cursor = end;
  });
  if (cursor < label.length) segments.push({text: label.slice(cursor), match: false});
  return segments.length ? segments : [{text: label, match: false}];
}
